(() => {
  'use strict';

  /* =========================================================
     MADE BY MIN
     PRODUCT JAVASCRIPT
     Shopify Product Section
     ========================================================= */


  /* =========================================================
     MONEY
     ========================================================= */

  function money(cents) {
    const currency =
      document.body?.dataset?.currency || 'AUD';

    const locale =
      document.documentElement.lang || 'en-AU';

    const amount =
      Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return '$' + amount.toFixed(2);
    }
  }


  /* =========================================================
     IMAGE CACHE
     ========================================================= */

  const imageCache = new Map();


  function preloadImage(src) {
    if (!src) {
      return Promise.resolve(null);
    }

    if (imageCache.has(src)) {
      return imageCache.get(src);
    }

    const promise = new Promise((resolve) => {
      const img = new Image();

      img.decoding = 'async';

      img.onload = () => {
        if (typeof img.decode === 'function') {
          img.decode()
            .catch(() => {})
            .finally(() => {
              resolve(img);
            });
        } else {
          resolve(img);
        }
      };

      img.onerror = () => {
        resolve(null);
      };

      img.src = src;
    });

    imageCache.set(src, promise);

    return promise;
  }


  /* =========================================================
     REDUCED MOTION
     ========================================================= */

  function prefersReducedMotion() {
    return window.matchMedia &&
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
  }


  /* =========================================================
     GALLERY PRELOAD
     ========================================================= */

  function preloadGalleryImages(thumbs) {
    if (!thumbs.length) {
      return;
    }

    const firstImage =
      thumbs[0]?.dataset?.image;

    preloadImage(firstImage);

    thumbs
      .slice(1, 3)
      .forEach((thumb) => {
        preloadImage(
          thumb?.dataset?.image
        );
      });

    const loadRemaining = () => {
      thumbs
        .slice(3)
        .forEach((thumb) => {
          preloadImage(
            thumb?.dataset?.image
          );
        });
    };

    if (
      'requestIdleCallback' in window
    ) {
      window.requestIdleCallback(
        loadRemaining
      );
    } else {
      window.setTimeout(
        loadRemaining,
        300
      );
    }
  }


  /* =========================================================
     PRODUCT INITIALIZATION
     ========================================================= */

  function init(root) {
    if (!root) {
      return;
    }

    /*
     * Prevent duplicate initialization
     */

    if (
      root.dataset.mbmProductReady === 'true'
    ) {
      return;
    }

    root.dataset.mbmProductReady = 'true';


    /* =======================================================
       GALLERY
       ======================================================= */

    const gallery =
      root.querySelector(
        '.product-gallery'
      );

    const mainImage =
      root.querySelector(
        '.gallery-main-image'
      );

    const thumbs = [
      ...root.querySelectorAll(
        '.gallery-thumb'
      )
    ];

    const prev =
      root.querySelector(
        '[id^="galleryPrev-"]'
      );

    const next =
      root.querySelector(
        '[id^="galleryNext-"]'
      );

    let imageIndex = 0;


    /*
     * Find active thumbnail
     */

    const activeIndex =
      thumbs.findIndex(
        (thumb) =>
          thumb.classList.contains('active')
      );

    if (activeIndex >= 0) {
      imageIndex = activeIndex;
    }


    /*
     * Preload gallery
     */

    preloadGalleryImages(
      thumbs
    );


    /* =======================================================
       THUMBNAIL STATE
       ======================================================= */

    function updateThumbnailState(index) {
      thumbs.forEach(
        (thumb, i) => {
          const active =
            i === index;

          thumb.classList.toggle(
            'active',
            active
          );

          thumb.setAttribute(
            'aria-current',
            active ? 'true' : 'false'
          );
        }
      );
    }


    /* =======================================================
       SET IMAGE
       ======================================================= */

    async function setImage(
      index,
      animate = true
    ) {
      if (
        !mainImage ||
        !thumbs.length
      ) {
        return;
      }

      /*
       * Infinite gallery
       */

      imageIndex =
        (
          index +
          thumbs.length
        ) %
        thumbs.length;

      const thumb =
        thumbs[imageIndex];

      if (!thumb) {
        return;
      }

      const src =
        thumb.dataset.image;

      if (!src) {
        return;
      }

      /*
       * Update thumbnail immediately
       */

      updateThumbnailState(
        imageIndex
      );

      /*
       * Preload
       */

      const loadedImage =
        await preloadImage(src);

      /*
       * If image failed to preload,
       * use normal browser loading.
       */

      if (!loadedImage) {
        mainImage.src = src;
        return;
      }

      /*
       * Reduced motion
       */

      if (
        !animate ||
        prefersReducedMotion()
      ) {
        mainImage.src =
          loadedImage.src;

        return;
      }


      /* =====================================================
         GSAP IMAGE TRANSITION
         ===================================================== */

      if (
        window.gsap &&
        typeof window.gsap.to === 'function'
      ) {
        window.gsap.killTweensOf(
          mainImage
        );

        window.gsap.to(
          mainImage,
          {
            opacity: 0,
            duration: 0.12,
            ease: 'power1.out',

            onComplete: () => {
              mainImage.src =
                loadedImage.src;

              window.gsap.to(
                mainImage,
                {
                  opacity: 1,
                  duration: 0.22,
                  ease: 'power2.out'
                }
              );
            }
          }
        );
      } else {
        mainImage.src =
          loadedImage.src;
      }


      /*
       * Preload adjacent images
       */

      const previousIndex =
        (
          imageIndex -
          1 +
          thumbs.length
        ) %
        thumbs.length;

      const nextIndex =
        (
          imageIndex +
          1
        ) %
        thumbs.length;

      preloadImage(
        thumbs[previousIndex]?.dataset?.image
      );

      preloadImage(
        thumbs[nextIndex]?.dataset?.image
      );
    }


    /* =======================================================
       THUMBNAIL EVENTS
       ======================================================= */

    thumbs.forEach(
      (thumb, index) => {

        /*
         * Mouse hover preload
         */

        thumb.addEventListener(
          'mouseenter',
          () => {
            preloadImage(
              thumb.dataset.image
            );
          },
          {
            passive: true
          }
        );


        /*
         * Touch preload
         */

        thumb.addEventListener(
          'touchstart',
          () => {
            preloadImage(
              thumb.dataset.image
            );
          },
          {
            passive: true
          }
        );


        /*
         * Click
         */

        thumb.addEventListener(
          'click',
          () => {
            setImage(
              index,
              true
            );
          }
        );

      }
    );


    /* =======================================================
       PREVIOUS BUTTON
       ======================================================= */

    if (prev) {
      prev.addEventListener(
        'click',
        () => {
          setImage(
            imageIndex - 1,
            true
          );
        }
      );
    }


    /* =======================================================
       NEXT BUTTON
       ======================================================= */

    if (next) {
      next.addEventListener(
        'click',
        () => {
          setImage(
            imageIndex + 1,
            true
          );
        }
      );
    }


    /* =======================================================
       KEYBOARD GALLERY
       ======================================================= */

    if (gallery) {
      /*
       * Make gallery keyboard accessible
       */

      if (
        !gallery.hasAttribute('tabindex')
      ) {
        gallery.setAttribute(
          'tabindex',
          '0'
        );
      }

      gallery.addEventListener(
        'keydown',
        (event) => {

          /*
           * Don't interfere with buttons
           */

          if (
            event.target !== gallery
          ) {
            return;
          }

          if (
            event.key === 'ArrowLeft'
          ) {
            event.preventDefault();

            setImage(
              imageIndex - 1,
              true
            );
          }

          if (
            event.key === 'ArrowRight'
          ) {
            event.preventDefault();

            setImage(
              imageIndex + 1,
              true
            );
          }
        }
      );
    }


    /* =======================================================
       VARIANTS
       ======================================================= */

    const variantsEl =
      root.querySelector(
        '[data-variants-json]'
      );

    let variants = [];

    try {
      variants =
        JSON.parse(
          variantsEl?.textContent || '[]'
        );

      if (!Array.isArray(variants)) {
        variants = [];
      }
    } catch (error) {
      console.error(
        'MBM Product: Invalid variant JSON',
        error
      );

      variants = [];
    }


    /* =======================================================
       FORM ELEMENTS
       ======================================================= */

    const form =
      root.querySelector(
        '.mbm-product-form'
      );

    const variantInput =
      form?.querySelector(
        '[data-variant-id]'
      );

    const quantityInput =
      form?.querySelector(
        '[data-quantity-input]'
      );

    const priceEl =
      root.querySelector(
        '[data-product-price]'
      );

    const compareEl =
      root.querySelector(
        '[data-product-compare]'
      );

    const addBtn =
      root.querySelector(
        '[data-add-to-cart]'
      );

    const addLabel =
      root.querySelector(
        '[data-add-label]'
      );


    /* =======================================================
       SELECTED OPTIONS
       ======================================================= */

    const selectedOptions = {};


    /* =======================================================
       INITIALIZE OPTION VALUES
       ======================================================= */

    root
      .querySelectorAll(
        '[data-option-group]'
      )
      .forEach(
        (group) => {

          const index =
            Number(
              group.dataset.optionIndex
            );

          const activeButton =
            group.querySelector(
              '[data-option-value].active'
            );

          const firstButton =
            group.querySelector(
              '[data-option-value]'
            );

          const selected =
            activeButton?.dataset?.optionValue ||
            firstButton?.dataset?.optionValue ||
            '';

          if (selected) {
            selectedOptions[index] =
              selected;
          }


          /* =================================================
             OPTION BUTTONS
             ================================================= */

          group
            .querySelectorAll(
              '[data-option-value]'
            )
            .forEach(
              (button) => {

                button.addEventListener(
                  'click',
                  () => {

                    /*
                     * Remove active
                     */

                    group
                      .querySelectorAll(
                        '[data-option-value]'
                      )
                      .forEach(
                        (item) => {
                          item.classList.remove(
                            'active'
                          );

                          item.setAttribute(
                            'aria-pressed',
                            'false'
                          );
                        }
                      );


                    /*
                     * Activate clicked
                     */

                    button.classList.add(
                      'active'
                    );

                    button.setAttribute(
                      'aria-pressed',
                      'true'
                    );


                    /*
                     * Save option
                     */

                    selectedOptions[index] =
                      button.dataset.optionValue;


                    /*
                     * Update selected label
                     */

                    const label =
                      group.querySelector(
                        '[data-selected-option]'
                      );

                    if (label) {
                      label.textContent =
                        button.dataset.optionValue;
                    }


                    /*
                     * Update variant
                     */

                    updateVariant();
                  }
                );

              }
            );

        }
      );


    /* =======================================================
       FIND VARIANT
       ======================================================= */

    function findMatchingVariant() {
      if (!variants.length) {
        return null;
      }

      return variants.find(
        (variant) => {

          const optionValues = [
            variant.option1,
            variant.option2,
            variant.option3
          ];

          return optionValues.every(
            (value, index) => {

              /*
               * Empty option = no restriction
               */

              if (
                value === null ||
                value === undefined ||
                value === ''
              ) {
                return true;
              }

              return (
                selectedOptions[index] ===
                value
              );
            }
          );
        }
      );
    }


    /* =======================================================
       UPDATE VARIANT
       ======================================================= */

    function updateVariant() {
      if (!variants.length) {
        return;
      }

      const variant =
        findMatchingVariant();

      /*
       * No matching variant
       */

      if (!variant) {

        if (addBtn) {
          addBtn.disabled = true;
        }

        if (addLabel) {
          addLabel.textContent =
            'Unavailable';
        }

        return;
      }


      /* =====================================================
         VARIANT ID
         ===================================================== */

      if (variantInput) {
        variantInput.value =
          variant.id;
      }


      /* =====================================================
         PRICE
         ===================================================== */

      if (priceEl) {
        priceEl.textContent =
          money(variant.price);
      }


      /* =====================================================
         COMPARE PRICE
         ===================================================== */

      if (compareEl) {

        const comparePrice =
          Number(
            variant.compare_at_price || 0
          );

        const variantPrice =
          Number(
            variant.price || 0
          );

        if (
          comparePrice > variantPrice
        ) {
          compareEl.hidden = false;

          compareEl.textContent =
            money(comparePrice);
        } else {
          compareEl.hidden = true;
          compareEl.textContent = '';
        }
      }


      /* =====================================================
         AVAILABILITY
         ===================================================== */

      const available =
        Boolean(
          variant.available
        );

      if (addBtn) {
        addBtn.disabled =
          !available;
      }

      if (addLabel) {
        addLabel.textContent =
          available
            ? 'Add to Cart'
            : 'Sold Out';
      }


      /* =====================================================
         VARIANT FEATURED IMAGE
         ===================================================== */

      if (
        variant.featured_image?.src &&
        thumbs.length
      ) {

        const variantSrc =
          variant.featured_image.src;

        /*
         * Remove query string
         */

        const variantFilename =
          variantSrc
            .split('?')[0]
            .split('/')
            .pop();

        if (variantFilename) {

          const match =
            thumbs.findIndex(
              (thumb) => {

                const thumbSrc =
                  thumb.dataset.image || '';

                const cleanThumb =
                  thumbSrc
                    .split('?')[0];

                return (
                  cleanThumb.includes(
                    variantFilename
                  )
                );
              }
            );

          if (match >= 0) {
            setImage(
              match,
              true
            );
          }
        }
      }
    }


    /* =======================================================
       QUANTITY
       ======================================================= */

    let qty = Number(
      quantityInput?.value || 1
    );

    if (
      !Number.isFinite(qty)
    ) {
      qty = 1;
    }

    const qtyValue =
      root.querySelector(
        '[data-qty-value]'
      );


    function syncQty() {

      /*
       * Minimum 1
       * Maximum 10
       */

      qty =
        Math.max(
          1,
          Math.min(
            10,
            Math.floor(qty)
          )
        );


      if (qtyValue) {
        qtyValue.textContent =
          qty;
      }


      if (quantityInput) {
        quantityInput.value =
          qty;
      }
    }


    /* =======================================================
       QUANTITY MINUS
       ======================================================= */

    root
      .querySelector(
        '[data-qty-minus]'
      )
      ?.addEventListener(
        'click',
        () => {

          qty -= 1;

          syncQty();
        }
      );


    /* =======================================================
       QUANTITY PLUS
       ======================================================= */

    root
      .querySelector(
        '[data-qty-plus]'
      )
      ?.addEventListener(
        'click',
        () => {

          qty += 1;

          syncQty();
        }
      );


    syncQty();


    /* =======================================================
       WISHLIST
       ======================================================= */

    const wishlist =
      root.querySelector(
        '[data-wishlist]'
      );

    if (wishlist) {

      wishlist.addEventListener(
        'click',
        () => {

          const active =
            wishlist.classList.toggle(
              'active'
            );

          wishlist.setAttribute(
            'aria-pressed',
            active ? 'true' : 'false'
          );
        }
      );

      if (
        !wishlist.hasAttribute(
          'aria-pressed'
        )
      ) {
        wishlist.setAttribute(
          'aria-pressed',
          'false'
        );
      }
    }


    /* =======================================================
       ACCORDION
       ======================================================= */

    const accordionItems = [
      ...root.querySelectorAll(
        '.info-accordion-item'
      )
    ];


    accordionItems.forEach(
      (item) => {

        const trigger =
          item.querySelector(
            '.info-accordion-trigger'
          );

        const panel =
          item.querySelector(
            '.info-accordion-panel'
          );

        if (
          !trigger ||
          !panel
        ) {
          return;
        }


        const isInitiallyOpen =
          item.classList.contains(
            'open'
          );


        /*
         * Accessibility
         */

        trigger.setAttribute(
          'aria-expanded',
          isInitiallyOpen
            ? 'true'
            : 'false'
        );


        /*
         * Panel ID
         */

        if (!panel.id) {

          panel.id =
            `accordion-panel-${Math.random()
              .toString(36)
              .slice(2, 10)}`;
        }

        trigger.setAttribute(
          'aria-controls',
          panel.id
        );


        /*
         * Initial panel state
         */

        if (isInitiallyOpen) {

          panel.setAttribute(
            'aria-hidden',
            'false'
          );

          panel.style.maxHeight =
            panel.scrollHeight + 'px';

        } else {

          panel.setAttribute(
            'aria-hidden',
            'true'
          );

          panel.style.maxHeight =
            '0px';
        }


        /* ===================================================
           ACCORDION CLICK
           =================================================== */

        trigger.addEventListener(
          'click',
          (event) => {

            event.preventDefault();

            const isOpen =
              item.classList.contains(
                'open'
              );


            /*
             * Close current
             */

            if (isOpen) {

              item.classList.remove(
                'open'
              );

              trigger.setAttribute(
                'aria-expanded',
                'false'
              );

              panel.setAttribute(
                'aria-hidden',
                'true'
              );

              panel.style.maxHeight =
                '0px';

              return;
            }


            /*
             * Close other accordions
             */

            accordionItems.forEach(
              (otherItem) => {

                if (
                  otherItem === item
                ) {
                  return;
                }

                const otherTrigger =
                  otherItem.querySelector(
                    '.info-accordion-trigger'
                  );

                const otherPanel =
                  otherItem.querySelector(
                    '.info-accordion-panel'
                  );

                otherItem.classList.remove(
                  'open'
                );

                if (otherTrigger) {
                  otherTrigger.setAttribute(
                    'aria-expanded',
                    'false'
                  );
                }

                if (otherPanel) {

                  otherPanel.setAttribute(
                    'aria-hidden',
                    'true'
                  );

                  otherPanel.style.maxHeight =
                    '0px';
                }
              }
            );


            /*
             * Open selected accordion
             */

            item.classList.add(
              'open'
            );

            trigger.setAttribute(
              'aria-expanded',
              'true'
            );

            panel.setAttribute(
              'aria-hidden',
              'false'
            );


            /*
             * Calculate height
             */

            requestAnimationFrame(
              () => {

                panel.style.maxHeight =
                  panel.scrollHeight + 'px';
              }
            );

          }
        );

      }
    );


    /* =======================================================
       ACCORDION RESIZE
       ======================================================= */

    const resizeHandler = () => {

      root
        .querySelectorAll(
          '.info-accordion-item.open .info-accordion-panel'
        )
        .forEach(
          (panel) => {

            panel.style.maxHeight =
              panel.scrollHeight + 'px';
          }
        );
    };


    window.addEventListener(
      'resize',
      resizeHandler,
      {
        passive: true
      }
    );


    /*
     * Store handler so theme editor
     * can clean it up if required.
     */

    root._mbmResizeHandler =
      resizeHandler;


    /* =======================================================
       TABS
       ======================================================= */

    const tabButtons = [
      ...root.querySelectorAll(
        '.tab-btn'
      )
    ];

    const tabPanels = [
      ...root.querySelectorAll(
        '.tab-panel'
      )
    ];


    tabButtons.forEach(
      (button) => {

        button.addEventListener(
          'click',
          () => {

            const name =
              button.dataset.tab;

            tabButtons.forEach(
              (btn) => {

                const active =
                  btn === button;

                btn.classList.toggle(
                  'active',
                  active
                );

                btn.setAttribute(
                  'aria-selected',
                  active
                    ? 'true'
                    : 'false'
                );
              }
            );


            tabPanels.forEach(
              (panel) => {

                const active =
                  panel.id.includes(
                    'tab-' + name
                  );

                panel.classList.toggle(
                  'active',
                  active
                );

                panel.hidden =
                  !active;
              }
            );
          }
        );

      }
    );


    /* =======================================================
       ADD TO CART
       ======================================================= */

    if (form) {

      form.addEventListener(
        'submit',
        async (event) => {

          event.preventDefault();


          /*
           * Make sure variant exists
           */

          if (
            !variantInput?.value
          ) {
            return;
          }


          /*
           * Don't submit sold-out product
           */

          if (
            addBtn?.disabled
          ) {
            return;
          }


          const originalLabel =
            addLabel?.textContent ||
            'Add to Cart';


          /*
           * Disable button
           */

          if (addBtn) {
            addBtn.disabled =
              true;
          }


          if (addLabel) {
            addLabel.textContent =
              'Adding…';
          }


          try {

            const quantity =
              Number(
                quantityInput?.value || 1
              );


            /* ===============================================
               ADD ITEM
               =============================================== */

            const response =
              await fetch(
                '/cart/add.js',
                {
                  method: 'POST',

                  headers: {
                    'Content-Type':
                      'application/x-www-form-urlencoded; charset=UTF-8',

                    'Accept':
                      'application/json'
                  },

                  body:
                    new URLSearchParams({
                      id:
                        String(
                          variantInput.value
                        ),

                      quantity:
                        String(
                          Math.max(
                            1,
                            quantity
                          )
                        )
                    })
                }
              );


            if (!response.ok) {

              let errorMessage =
                'Unable to add product';

              try {

                const errorData =
                  await response.json();

                if (
                  errorData?.description
                ) {
                  errorMessage =
                    errorData.description;
                }

              } catch (jsonError) {
                /*
                 * Ignore JSON parsing errors.
                 */
              }

              throw new Error(
                errorMessage
              );
            }


            /*
             * Read add response
             */

            await response.json();


            /* ===============================================
               GET CURRENT CART
               =============================================== */

            const cartResponse =
              await fetch(
                '/cart.js',
                {
                  method: 'GET',

                  headers: {
                    'Accept':
                      'application/json'
                  }
                }
              );


            if (!cartResponse.ok) {
              throw new Error(
                'Unable to refresh cart'
              );
            }


            const cart =
              await cartResponse.json();


            /* ===============================================
               UPDATE CART COUNT
               =============================================== */

            updateCartCount(
              cart.item_count
            );


            /*
             * Dispatch Shopify-style event
             */

            document.dispatchEvent(
              new CustomEvent(
                'mbm:cart:updated',
                {
                  detail: {
                    cart: cart
                  }
                }
              )
            );


            /*
             * Success
             */

            if (addLabel) {
              addLabel.textContent =
                'Added ✓';
            }


            /*
             * Restore after delay
             */

            window.setTimeout(
              () => {

                if (addLabel) {
                  addLabel.textContent =
                    originalLabel;
                }

                if (addBtn) {

                  /*
                   * Check selected variant
                   * again before enabling.
                   */

                  const currentVariant =
                    findMatchingVariant();

                  addBtn.disabled =
                    currentVariant
                      ? !currentVariant.available
                      : false;
                }

              },
              1400
            );

          } catch (error) {

            console.error(
              'MBM Add to Cart:',
              error
            );


            if (addLabel) {
              addLabel.textContent =
                'Try Again';
            }


            if (addBtn) {
              addBtn.disabled =
                false;
            }


            window.setTimeout(
              () => {

                if (addLabel) {
                  addLabel.textContent =
                    originalLabel;
                }

              },
              1800
            );

          }

        }
      );
    }


    /* =======================================================
       CART COUNT
       ======================================================= */

    function updateCartCount(count) {

      /*
       * Generic data cart count
       */

      root
        .ownerDocument
        .querySelectorAll(
          '[data-cart-count]'
        )
        .forEach(
          (element) => {

            element.textContent =
              count;
          }
        );


      /*
       * Shopify cart bubble
       */

      root
        .ownerDocument
        .querySelectorAll(
          '.cart-count-bubble'
        )
        .forEach(
          (bubble) => {

            const spans =
              bubble.querySelectorAll(
                'span[aria-hidden="true"]'
              );

            if (spans.length) {

              spans[0].textContent =
                count;
            } else {

              bubble.textContent =
                count;
            }


            /*
             * Show / hide bubble
             */

            bubble.hidden =
              Number(count) <= 0;
          }
        );


      /*
       * Common cart count selectors
       */

      root
        .ownerDocument
        .querySelectorAll(
          '[data-cart-count], .cart-count'
        )
        .forEach(
          (element) => {

            element.textContent =
              count;
          }
        );
    }


    /* =======================================================
       INITIAL VARIANT UPDATE
       ======================================================= */

    if (variants.length) {
      updateVariant();
    }


    /* =======================================================
       GSAP
       ======================================================= */

    initAnimations(
      root,
      gallery
    );


    /* =======================================================
       CLEANUP
       ======================================================= */

    root._mbmProductCleanup = () => {

      if (
        root._mbmResizeHandler
      ) {

        window.removeEventListener(
          'resize',
          root._mbmResizeHandler
        );

        root._mbmResizeHandler =
          null;
      }


      /*
       * Kill GSAP animations belonging
       * to this product section.
       */

      if (
        window.gsap
      ) {

        try {

          window.gsap.killTweensOf(
            root.querySelectorAll('*')
          );

        } catch (error) {
          /*
           * Ignore GSAP cleanup errors.
           */
        }
      }
    };
  }


  /* =========================================================
     GSAP ANIMATIONS
     ========================================================= */

  function initAnimations(
    root,
    gallery
  ) {

    if (
      !window.gsap ||
      !window.ScrollTrigger
    ) {
      return;
    }


    if (
      prefersReducedMotion()
    ) {
      return;
    }


    /*
     * Register ScrollTrigger
     */

    try {
      window.gsap.registerPlugin(
        window.ScrollTrigger
      );
    } catch (error) {
      console.warn(
        'MBM: Unable to register ScrollTrigger',
        error
      );
    }


    /* =======================================================
       GALLERY
       ======================================================= */

    if (gallery) {

      window.gsap.from(
        gallery,
        {
          opacity: 0,
          y: 24,
          duration: 0.8,
          ease: 'power3.out',
          delay: 0.1
        }
      );
    }


    /* =======================================================
       PRODUCT INFO
       ======================================================= */

    const infoPanel =
      root.querySelector(
        '.product-info-panel'
      );

    if (infoPanel) {

      const children = [
        ...infoPanel.children
      ];

      if (children.length) {

        window.gsap.from(
          children,
          {
            opacity: 0,
            y: 18,
            duration: 0.7,
            stagger: 0.06,
            ease: 'power3.out',
            delay: 0.2
          }
        );
      }
    }


    /* =======================================================
       RELATED HEADER
       ======================================================= */

    const relatedHeader =
      root.querySelector(
        '[id^="relatedHeader-"]'
      );

    if (relatedHeader) {

      window.gsap.from(
        relatedHeader,
        {
          opacity: 0,
          y: 24,
          duration: 0.8,
          ease: 'power3.out',

          scrollTrigger: {
            trigger:
              relatedHeader,

            start:
              'top 85%',

            once:
              true
          }
        }
      );
    }


    /* =======================================================
       RELATED GRID
       ======================================================= */

    const relatedGrid =
      root.querySelector(
        '[id^="relatedGrid-"]'
      );

    if (relatedGrid) {

      const cards =
        relatedGrid.querySelectorAll(
          '.related-card'
        );

      if (cards.length) {

        window.gsap.from(
          cards,
          {
            opacity: 0,
            y: 32,
            duration: 0.8,
            stagger: 0.12,
            ease: 'power3.out',

            scrollTrigger: {
              trigger:
                relatedGrid,

              start:
                'top 85%',

              once:
                true
            }
          }
        );
      }
    }
  }


  /* =========================================================
     BOOT
     ========================================================= */

  function boot() {

    document
      .querySelectorAll(
        '[data-mbm-product]'
      )
      .forEach(
        (root) => {

          init(root);

        }
      );
  }


  /* =========================================================
     DOM READY
     ========================================================= */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true
      }
    );

  } else {

    boot();

  }


  /* =========================================================
     SHOPIFY THEME EDITOR
     ========================================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      const target =
        event.target;

      if (!target) {
        return;
      }


      /*
       * Section itself
       */

      if (
        target.matches?.(
          '[data-mbm-product]'
        )
      ) {

        /*
         * Cleanup old instance
         */

        if (
          typeof target._mbmProductCleanup ===
          'function'
        ) {
          target._mbmProductCleanup();
        }


        /*
         * Reset initialization
         */

        target.dataset.mbmProductReady =
          '';


        init(target);

        return;
      }


      /*
       * Product section inside
       * loaded section
       */

      target
        .querySelectorAll?.(
          '[data-mbm-product]'
        )
        .forEach(
          (root) => {

            if (
              typeof root._mbmProductCleanup ===
              'function'
            ) {
              root._mbmProductCleanup();
            }

            root.dataset.mbmProductReady =
              '';

            init(root);
          }
        );
    }
  );


  /* =========================================================
     SHOPIFY SECTION UNLOAD
     ========================================================= */

  document.addEventListener(
    'shopify:section:unload',
    (event) => {

      const target =
        event.target;

      if (!target) {
        return;
      }


      /*
       * Cleanup section itself
       */

      if (
        target.matches?.(
          '[data-mbm-product]'
        )
      ) {

        if (
          typeof target._mbmProductCleanup ===
          'function'
        ) {

          target._mbmProductCleanup();
        }

        return;
      }


      /*
       * Cleanup nested product sections
       */

      target
        .querySelectorAll?.(
          '[data-mbm-product]'
        )
        .forEach(
          (root) => {

            if (
              typeof root._mbmProductCleanup ===
              'function'
            ) {

              root._mbmProductCleanup();
            }
          }
        );
    }
  );


  /* =========================================================
     SHOPIFY VARIANT CHANGE EVENT
     ========================================================= */

  document.addEventListener(
    'variant:change',
    (event) => {

      /*
       * This is intentionally lightweight.
       *
       * If another Shopify app/theme changes
       * the variant, our normal option controls
       * remain responsible for updating the UI.
       */

      if (
        !event.detail
      ) {
        return;
      }

    }
  );

})();